import React from 'react';
import { GripVertical, Star, Users, Film as FilmIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { stripKey } from '@/lib/stripColours';

export { stripKey };

/**
 * One scene = one strip (M2.1). Colour follows the industry board
 * convention so an AD reads it without a legend:
 *   white = INT/day · yellow = EXT/day · blue = INT/night · green = EXT/night
 * The key comes from the backend (`scene.strip`) — see services/stripboard.py —
 * so the board, the PDF and the DOOD never disagree.
 */
export const STRIP_TONES = {
  white:   { bar: 'bg-zinc-300 dark:bg-zinc-500',  bg: 'bg-card',                                     label: 'INT / Day' },
  yellow:  { bar: 'bg-yellow-400',                 bg: 'bg-yellow-50 dark:bg-yellow-500/10',           label: 'EXT / Day' },
  blue:    { bar: 'bg-blue-500',                   bg: 'bg-blue-50 dark:bg-blue-500/10',               label: 'INT / Night' },
  green:   { bar: 'bg-green-500',                  bg: 'bg-green-50 dark:bg-green-500/10',             label: 'EXT / Night' },
  neutral: { bar: 'bg-muted-foreground/30',        bg: 'bg-card',                                     label: 'Unspecified' },
};

const IE = { interior: 'INT', exterior: 'EXT' };
const TOD = { day: 'DAY', night: 'NIGHT', morning: 'MORNING', evening: 'EVENING', afternoon: 'AFTERNOON' };

export const StripLegend = ({ className }) => (
  <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground', className)} data-testid="strip-legend">
    {['white', 'yellow', 'blue', 'green'].map((k) => (
      <span key={k} className="inline-flex items-center gap-1">
        <span className={cn('inline-block w-2.5 h-3 rounded-[2px]', STRIP_TONES[k].bar)} />
        {STRIP_TONES[k].label}
      </span>
    ))}
  </div>
);

/**
 * Props: scene, index (position within its day), draggable handlers,
 * dropIndicator: 'before' | 'after' | null — where a dragged strip would land.
 */
const Strip = ({
  scene, onClick, draggable, onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop,
  isDragging, dropIndicator, compact,
}) => {
  const tone = STRIP_TONES[stripKey(scene)];
  const shots = Number(scene.shot_count) || 0;
  const covered = Number(scene.covered_count) || 0;
  const cast = Array.isArray(scene.cast_ids) ? scene.cast_ids.length : 0;
  const slug = scene.scene_name || scene.location || scene.description || `Scene ${scene.scene_number || '—'}`;
  const ie = IE[scene.interior_exterior];
  const tod = TOD[scene.time_of_day];
  const statusDot = { not_ready: 'bg-destructive', ready: 'bg-success', shot: 'bg-warning', done: 'bg-success' }[scene.status] || 'bg-muted-foreground/40';

  return (
    <div
      className={cn('relative', dropIndicator === 'before' && 'pt-2', dropIndicator === 'after' && 'pb-2')}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid={`strip-${scene.id}`}
    >
      {dropIndicator && (
        <span
          className={cn('absolute left-0 right-0 h-0.5 rounded bg-primary', dropIndicator === 'before' ? 'top-0' : 'bottom-0')}
          aria-hidden
        />
      )}
      <article
        draggable={draggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={() => onClick?.(scene)}
        title={`${tone.label}${scene.location ? ` · ${scene.location}` : ''}`}
        className={cn(
          'group flex items-stretch rounded-md border border-border overflow-hidden cursor-pointer transition-all',
          'hover:border-primary/50 hover:shadow-sm',
          tone.bg,
          isDragging && 'opacity-40 scale-[0.99]',
        )}
      >
        <span className={cn('w-1.5 shrink-0', tone.bar)} />
        <div className={cn('flex items-center gap-2 min-w-0 flex-1 px-2', compact ? 'py-1' : 'py-1.5')}>
          <span className="shrink-0 w-9 text-center font-display font-bold text-sm tabular-nums leading-none">
            {scene.scene_number || '—'}
          </span>
          <span className="shrink-0 text-[9px] font-bold tracking-wider text-muted-foreground w-[4.5rem] leading-tight">
            {ie || '—'}{tod ? <><br />{tod}</> : null}
          </span>
          <span className={cn('shrink-0 size-1.5 rounded-full', statusDot)} title={scene.status} />
          <span className="min-w-0 flex-1 text-xs font-medium truncate">
            {slug}
            {scene.location && scene.scene_name && scene.location !== scene.scene_name && (
              <span className="text-muted-foreground font-normal"> · {scene.location}</span>
            )}
          </span>
          {scene.is_golden && <Star className="size-3 text-warning fill-current shrink-0" />}
          <span className="hidden sm:inline-flex items-center gap-1 text-[10px] tabular-nums text-muted-foreground shrink-0" title="Cast tagged on this scene">
            <Users className="size-3" />{cast}
          </span>
          <span
            className={cn(
              'hidden sm:inline-flex items-center gap-1 text-[10px] tabular-nums shrink-0 px-1.5 py-0.5 rounded-full border',
              shots === 0 ? 'text-muted-foreground border-border' : covered === shots ? 'text-success border-success/40 bg-success/10' : 'text-foreground border-border',
            )}
            title="Shots covered / planned"
          >
            <FilmIcon className="size-3" />{covered}/{shots}
          </span>
          {draggable && <GripVertical className="size-3.5 text-muted-foreground/40 group-hover:text-muted-foreground cursor-grab active:cursor-grabbing shrink-0" />}
        </div>
      </article>
    </div>
  );
};

export default Strip;
